import { apiResponse } from '@crowdex/host-kit/renderer/http';

/**
 * The user's model per agent, an account preference the server keeps (synced across devices):
 * the model last picked for the agent and the models it last listed. Server-started turns run it.
 */
export type AgentModelPreference = { model: string | null; listed: string[] };

export async function fetchAgentModelPreference(
  agentId: string,
): Promise<AgentModelPreference | null> {
  try {
    const response = await apiResponse(`/api/agent/models?agentId=${encodeURIComponent(agentId)}`, {
      credentials: 'include',
    });
    const value = (await response.json()) as Partial<AgentModelPreference> | null;
    return {
      model: typeof value?.model === 'string' ? value.model : null,
      listed: Array.isArray(value?.listed)
        ? value.listed.filter((entry): entry is string => typeof entry === 'string')
        : [],
    };
  } catch {
    return null;
  }
}

/** Records the model picked for the agent, or the models it listed; best effort. */
export async function saveAgentModelPreference(
  agentId: string,
  change: { model?: string; listed?: readonly string[] },
): Promise<void> {
  await apiResponse('/api/agent/models', {
    method: 'PUT',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ agentId, ...change }),
  }).catch(() => undefined);
}

/** Saves a conversation's model now, so a turn the server starts meanwhile runs it too. */
export async function saveConversationModel(
  conversationId: string,
  modelId: string,
): Promise<void> {
  await apiResponse(`/api/conversations/${encodeURIComponent(conversationId)}/settings`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ modelId }),
  }).catch(() => undefined);
}
